import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, useTheme } from "@mui/material";
import { Lock } from "lucide-react";
import ListItemCardCst from "@components/ListItemCardCst";
import { useCardActions } from "@hooks/useCardActions";
import { mapUserToCard } from "../mapUserToCard";

export interface UserItemHandlers {
  onEnroll: (user: any) => void;
  onViewLO: (user: any) => void;
  onWelcomeMail: (user: any) => void;
  onImpersonate: (user: any) => void;
  onEdit: (user: any) => void;
  onBlock: (user: any) => void;
  onDelete: (user: any) => void;
}

interface UserItemProps extends UserItemHandlers {
  user: any;
  corporateId?: string;
  selected: boolean;
  onSelect: (next: boolean) => void;
  onOpenDetail?: (user: any) => void;
}

const UserItem: React.FC<UserItemProps> = ({
  user,
  corporateId,
  selected,
  onSelect,
  onEnroll,
  onViewLO,
  onWelcomeMail,
  onImpersonate,
  onEdit,
  onBlock,
  onDelete,
  onOpenDetail,
}) => {
  const overrides = useMemo(
    () => ({
      enroll: () => onEnroll(user),
      "view-lo": () => onViewLO(user),
      "welcome-mail": () => onWelcomeMail(user),
      impersonate: () => onImpersonate(user),
      edit: () => onEdit(user),
      block: () => onBlock(user),
      delete: () => onDelete(user),
    }),
    [
      user,
      onEnroll,
      onViewLO,
      onWelcomeMail,
      onImpersonate,
      onEdit,
      onBlock,
      onDelete,
    ],
  );

  const moreActions = useCardActions("user", user, { corporateId, overrides });

  const { t } = useTranslation("input");
  const { t: tCard } = useTranslation("list-item-card");
  const theme = useTheme();

  const cardProps = mapUserToCard(user);

  const actions = onOpenDetail
    ? [
        {
          label: tCard("actions.details"),
          variant: "outlined" as const,
          onClick: () => onOpenDetail(user),
        },
      ]
    : undefined;

  const status = String(user.status || "").toLowerCase();
  const titleAdornment =
    status === "suspended" ? (
      <Tooltip title={t("options.status_user.suspended")}>
        <Lock size={14} color={theme.palette.error.main} />
      </Tooltip>
    ) : undefined;

  return (
    <ListItemCardCst
      {...cardProps}
      titleAdornment={titleAdornment}
      selectable
      selected={selected}
      onSelect={onSelect}
      actions={actions}
      moreActions={moreActions}
    />
  );
};

export default UserItem;
